import { createFileRoute, Link } from '@tanstack/react-router';
import { LegalDocument, LegalSection } from '@/components/legal/legal-document';

export const Route = createFileRoute('/terms-of-use')({
  component: TermsOfUsePage,
});

const EFFECTIVE_DATE = 'November 1, 2026';

function TermsOfUsePage() {
  return (
    <LegalDocument
      title="Terms of Use | Boundr"
      description="Terms of Use for Boundr land monitoring — best-effort inspections, subscriptions, refunds, liability, and Washington State governing law."
      path="/terms-of-use"
      heading="Terms of Use"
      effectiveDate={EFFECTIVE_DATE}
    >
      <LegalSection id="agreement" title="1. Agreement">
        <p>
          These Terms of Use (&quot;Terms&quot;) are a binding agreement between you and Yashna Labs
          LLC (&quot;Boundr,&quot; &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;) for use of the
          Boundr land monitoring / plot inspection service and related websites and dashboards (the
          &quot;Service&quot;). Boundr is operated from Washington, USA, and serves customers
          worldwide.
        </p>
        <p>
          By creating an account, starting a subscription, or using the Service, you accept these
          Terms. If you do not agree, do not use the Service. The English version of these Terms
          controls; any translation is for convenience only.
        </p>
      </LegalSection>

      <LegalSection id="eligibility" title="2. Eligibility and authority">
        <p>
          You must be at least 18 years old. By signing up, you represent that you are an adult and
          legally able to enter this agreement.
        </p>
        <p>
          You also represent that you are the owner of each plot you enroll, or that you are
          authorized under applicable law by the owner to enroll and manage that plot. You are
          responsible for ensuring that authority remains valid while the plot is monitored.
        </p>
      </LegalSection>

      <LegalSection id="service" title="3. The Service">
        <p>
          Boundr provides remote land monitoring: scheduled, best-effort on-ground plot inspections
          documented with photo and video evidence and delivered as a digital report. The Service
          currently covers land monitoring / plot inspection only.
        </p>
        <p>
          Visit timing windows (including first-report estimates shown in marketing materials) are
          best-effort targets, not guarantees or service-level agreements.
        </p>
        <p>
          Unless you provide additional access information, field personnel work from publicly
          accessible boundaries and vantage points. If a visit cannot be completed safely or
          access is blocked, that cycle is forfeited with no makeup visit and no refund for a
          report period already associated with a completed attempt or completed report, as
          described in the billing section below.
        </p>
      </LegalSection>

      <LegalSection id="subcontractors" title="4. Subcontractors and service partners">
        <p>
          Boundr may engage subcontractors and service partners to perform any part of the Service,
          including on-ground visits. Boundr does not employ field inspectors as its own employees;
          on-ground work is arranged through Boundr&apos;s service partners. Field personnel do not
          communicate directly with customers through Boundr. You agree that Boundr may change
          partners without naming individual inspectors to you.
        </p>
      </LegalSection>

      <LegalSection id="accounts" title="5. Accounts">
        <p>
          Keep your login credentials confidential. Accounts must not be shared. You are
          responsible for activity under your account. Notify us promptly via{' '}
          <Link to="/contact-us" className="text-ring underline-offset-4 hover:underline">
            Contact Us
          </Link>{' '}
          if you suspect unauthorized access.
        </p>
      </LegalSection>

      <LegalSection id="reports-ip" title="6. Reports, media, and intellectual property">
        <p>
          Inspection photos, video, checklist data, and reports (&quot;Inspection Materials&quot;)
          are created in connection with the Service. Boundr retains all rights in Inspection
          Materials and may use them for any purpose, including quality assurance, training, and
          product improvement, during the retention period described in our Privacy Policy.
        </p>
        <p>
          Subject to these Terms, you may download and use reports delivered to you (including
          providing them to advisors or courts) for your own property-management purposes. Reports
          are documentary records of observed conditions on a visit date; they are not a licensed
          surveyor&apos;s report, legal advice, or a guarantee of title, boundaries, or
          encumbrance status.
        </p>
        <p>
          Boundr does not retain Inspection Materials beyond the end of your subscription (see the
          Privacy Policy). Download what you need while your subscription is active.
        </p>
        <p>
          Boundr, the Boundr logo, and related marks are owned by Yashna Labs LLC. All rights
          reserved. You may not use our marks in a way that suggests endorsement or partnership
          without prior written permission.
        </p>
      </LegalSection>

      <LegalSection id="billing" title="7. Subscriptions, auto-renewal, cancellation, and refunds">
        <p>
          Land monitoring is offered on flexible monthly subscription terms and auto-renews at the
          then-current price until you cancel. There is no advance notice requirement for price
          changes; renewals bill at the price then in effect.
        </p>
        <p>
          Payments are processed by our payment gateway provider(s). Boundr does not provide a
          grace period after a failed or stopped payment — service stops when payment stops.
        </p>
        <p>
          You may cancel at any time from your dashboard (or by contacting us). Cancellation ends
          the subscription immediately.
        </p>
        <p>
          <span className="text-foreground">Refunds.</span> Boundr does not refund fees for
          services rendered. If a report for a paid period has been completed by our service
          partner, that period is non-refundable. If you cancel before a report for a paid period
          has been completed, you may be eligible for a refund of that unused period. Chargebacks
          and payment disputes do not expand these refund rules.
        </p>
      </LegalSection>

      <LegalSection id="disclaimer" title="8. Disclaimer of warranties">
        <p>
          THE SERVICE AND ALL REPORTS ARE PROVIDED ON A BEST-EFFORT BASIS, &quot;AS IS&quot; AND
          &quot;AS AVAILABLE,&quot; WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, OR
          STATUTORY, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND
          NON-INFRINGEMENT. Boundr does not warrant that inspections will detect every issue,
          encroachment, hazard, or change on a plot, or that reports are complete or error-free.
        </p>
      </LegalSection>

      <LegalSection id="liability" title="9. Limitation of liability">
        <p>
          To the maximum extent permitted by law, Boundr and its officers, members, contractors,
          and service partners are not liable for any indirect, incidental, special, consequential,
          exemplary, or punitive damages, or for any loss of profits, data, goodwill, or business
          opportunity, arising from the Service or any report — including missed findings, omitted
          data, GPS or media inaccuracy, delayed or forfeited visits, or reliance on a report.
        </p>
        <p>
          Boundr&apos;s total liability for any claim relating to the Service will not exceed the
          fees you actually paid to Boundr for the Service giving rise to the claim.
        </p>
      </LegalSection>

      <LegalSection id="indemnity" title="10. Indemnification">
        <p>
          You will indemnify and hold harmless Yashna Labs LLC and its officers, members,
          contractors, and service partners from claims, damages, losses, and expenses (including
          reasonable attorneys&apos; fees) arising out of your misuse of the Service or reports,
          your breach of these Terms, or your lack of authority over an enrolled plot.
        </p>
      </LegalSection>

      <LegalSection id="acceptable-use" title="11. Acceptable use">
        <p>
          You may not misuse the Service, attempt unauthorized access, interfere with operations,
          scrape or reverse engineer the platform except where prohibited restrictions are
          disallowed by law, or use reports to harass others or commit unlawful acts.
        </p>
      </LegalSection>

      <LegalSection id="governing-law" title="12. Governing law and disputes">
        <p>
          These Terms are governed by the laws of the State of Washington, USA, without regard to
          conflict-of-law rules. Exclusive jurisdiction and venue for any dispute lie in the state
          or federal courts located in Washington, and you consent to personal jurisdiction there.
          Jury trial waiver applies to the extent permitted by law.
        </p>
      </LegalSection>

      <LegalSection id="changes" title="13. Changes">
        <p>
          We may update these Terms from time to time. The effective date above will change when we
          do. Continued use after an update constitutes acceptance of the revised Terms.
        </p>
      </LegalSection>

      <LegalSection id="contact" title="14. Contact">
        <p>
          The sole channel for legal notices, disputes, and support requests under these Terms is
          our{' '}
          <Link to="/contact-us" className="text-ring underline-offset-4 hover:underline">
            Contact Us
          </Link>{' '}
          form. Boundr / Yashna Labs LLC, Washington, USA.
        </p>
      </LegalSection>
    </LegalDocument>
  );
}
